/**
 * When adding roles, add the same `id` to `VALID_CAREER_JOB_IDS` in
 * `src/server/controllers/publicContactController.ts` so applications are accepted server-side.
 */
export type CareerJob = {
  id: string;
  title: string;
  team: string;
  location: string;
  employmentType: string;
  /** Short line for listing cards */
  summary: string;
  /**
   * If set, the sidebar card links here (e.g. Dover apply URL) instead of the internal job page.
   * Example: https://app.dover.com/apply/YourOrg/<opening-id>/
   */
  externalApplyUrl?: string;
  sections: { heading: string; paragraphs: string[] }[];
};

export const CAREER_JOBS: CareerJob[] = [
  {
    id: 'sales',
    title: 'Account Executive, Mid-Market',
    team: 'Sales',
    location: 'Remote (US)',
    employmentType: 'Full-time',
    summary: 'Own the full sales cycle for teams adopting pricing intelligence and experimentation.',
    // externalApplyUrl: 'https://app.dover.com/apply/YourOrg/<opening-id>/',
    sections: [
      {
        heading: 'About the role',
        paragraphs: [
          'You will help SAAS operators understand how pricing experiments and ML-driven recommendations translate into revenue. This is a consultative sales role: discovery, demos, mutual plans, and clean handoffs to onboarding.',
          'We care about clear communication, honest qualification, and building trust with technical and commercial buyers.',
        ],
      },
      {
        heading: 'What you will do',
        paragraphs: [
          'Run outbound and inbound conversations with growth, product, and revenue leaders.',
          'Partner with marketing on messaging, ICP, and pipeline; keep CRM and forecasting accurate.',
          'Coordinate proof-of-value steps with our customer and engineering teams.',
        ],
      },
      {
        heading: 'What we look for',
        paragraphs: [
          '3+ years selling B2B SAAS with evidence of hitting quota in a startup or scale-up environment.',
          'Comfort discussing analytics, experimentation, or pricing topics at a practical level.',
          'Organized, concise, and motivated by helping customers make good decisions—not pressure tactics.',
        ],
      },
    ],
  },
  {
    id: 'software-development',
    title: 'Software Engineer, Full Stack',
    team: 'Engineering',
    location: 'Remote',
    employmentType: 'Full-time',
    summary: 'Ship product across our TypeScript/React app, Node APIs, and data/ML integrations.',
    // externalApplyUrl: 'https://app.dover.com/apply/YourOrg/<opening-id>/',
    sections: [
      {
        heading: 'About the role',
        paragraphs: [
          'You will build features end to end: polished UI, reliable APIs, and the glue to our optimization and analytics pipelines. We value small, reviewable changes, clear ownership, and pragmatic tradeoffs.',
        ],
      },
      {
        heading: 'What you will do',
        paragraphs: [
          'Implement and refine customer-facing flows for pricing tables, experiments, and results.',
          'Collaborate on schema, performance, and observability for services our users depend on.',
          'Participate in design and code review; help keep the codebase approachable as we grow.',
        ],
      },
      {
        heading: 'What we look for',
        paragraphs: [
          'Strong TypeScript or JavaScript experience and comfort with React (or similar) on the client.',
          'Solid understanding of REST or RPC APIs, databases, and deploying to modern cloud environments.',
          'Curiosity about ML-adjacent workflows; you do not need to be a data scientist, but you should enjoy learning.',
        ],
      },
    ],
  },
];

export function getCareerJob(jobId: string): CareerJob | undefined {
  return CAREER_JOBS.find((j) => j.id === jobId);
}
